<template>
  <div class="bcsv">
    <div class="bcsv-head-row">
      <div>
        <h1 class="bcsv-h1">
          Batch Transfer
        </h1>
        <p class="bcsv-sub">
          อัปโหลดไฟล์ CSV เพื่อสร้าง Withdraw Note อัตโนมัติ ระบบจัดกลุ่มตาม group ให้เอง
        </p>
      </div>
      <button
        type="button"
        class="bcsv-btn bcsv-btn--primary"
        @click="showUploadModal = true">
        <svg
          width="15"
          height="15"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7">
          <path d="M8 3.5v9M3.5 8h9" />
        </svg>
        Upload CSV
      </button>
    </div>

    <div class="bcsv-card">
      <div class="bcsv-toolbar">
        <div class="bcsv-search">
          <svg
            width="15"
            height="15"
            viewBox="0 0 14 14"
            fill="none"
            stroke="#9AA3AE"
            stroke-width="1.5">
            <circle
              cx="6.2"
              cy="6.2"
              r="4" />
            <path d="M9.2 9.2 12 12" />
          </svg>
          <input
            v-model="search"
            type="text"
            placeholder="Search file name or uploader..."
            @input="onSearchInput">
        </div>
      </div>

      <div class="bcsv-tabs">
        <button
          v-for="item in tabs"
          :key="item.value"
          type="button"
          class="bcsv-tab"
          :class="{ 'bcsv-tab--on': query.uploadStatus === item.value }"
          @click="onTabChange(item.value)">
          {{ item.label }}
          <span class="bcsv-tab-count">{{ countFor(item.value) }}</span>
        </button>
      </div>

      <pagination-bar
        :page="query.page"
        :limit="query.limit"
        :total="totalItems"
        @update:page="onPage"
        @update:limit="onLimit" />

      <div class="bcsv-scroll">
        <div style="min-width: 990px;">
          <div class="bcsv-grid-head bcsv-list-grid">
            <div>Batch no.</div>
            <div>File name</div>
            <div>Upload status</div>
            <div>Withdraw Note</div>
            <div>Upload date</div>
            <div class="text-center">
              Uploaded by
            </div>
          </div>

          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            height="3"
            class="my-0" />

          <div
            v-for="item in items"
            :key="item.id"
            class="bcsv-grid-row bcsv-list-grid bcsv-row-link">
            <div>
              <router-link
                class="bcsv-batch-no bcsv-mono"
                :to="detailRoute(item)">
                {{ item.code }}
              </router-link>
            </div>
            <div style="min-width: 0;">
              <span
                class="bcsv-file-name"
                :title="item.fileName">
                {{ item.fileName }}
              </span>
            </div>
            <div>
              <span
                class="bcsv-chip"
                :style="uploadChipStyle(item.uploadStatus)">
                {{ uploadStatusLabel(item.uploadStatus) }}
              </span>
            </div>
            <div>
              <span
                :class="isWithdrawNoteStatusPlain(item.withdrawNoteStatus) ? 'bcsv-chip--plain' : 'bcsv-chip'"
                :style="withdrawNoteChipStyle(item.withdrawNoteStatus)">
                {{ withdrawNoteStatusLabel(item.withdrawNoteStatus) }}
              </span>
            </div>
            <div>
              <div class="bcsv-date">
                {{ item.uploadedAt | dateUserFormatMonth }}
              </div>
              <div class="bcsv-time bcsv-mono">
                {{ $dayjs(item.uploadedAt).format('HH:mm:ss') }}
              </div>
            </div>
            <div class="bcsv-cell-soft text-center">
              <span v-if="item.createdBy">{{ item.createdBy.email }}</span>
            </div>
          </div>

          <div
            v-if="loading"
            class="bcsv-empty">
            กำลังโหลดข้อมูล…
          </div>
          <div
            v-else-if="!items.length"
            class="bcsv-empty">
            ไม่พบไฟล์ที่ตรงกับเงื่อนไข
          </div>
        </div>
      </div>

      <pagination-bar
        foot
        :page="query.page"
        :limit="query.limit"
        :total="totalItems"
        @update:page="onPage"
        @update:limit="onLimit" />
    </div>

    <batch-transfer-modal
      v-model="showUploadModal"
      @uploaded="onUploaded" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import BatchTransferProvider from '@/resources/BatchTransferProvider'
import BatchTransferModal from '../components/BatchTransferModal.vue'
import PaginationBar from '../components/BatchTransferPaginationBar.vue'
import {
  UPLOAD_STATUS_TABS,
  uploadStatusLabel,
  uploadChipStyle,
  withdrawNoteStatusLabel,
  withdrawNoteChipStyle,
  isWithdrawNoteStatusPlain
} from '@/assets/js/BatchTransferStatusUtils'

const batchTransferProvider = new BatchTransferProvider()

export default {
  name: 'BatchTransfer',
  components: {
    BatchTransferModal,
    PaginationBar
  },
  data () {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      showUploadModal: false,
      search: '',
      tabs: UPLOAD_STATUS_TABS,
      counts: { all: 0, success: 0, failed: 0 },
      query: {
        page: 1,
        limit: 10,
        search: '',
        uploadStatus: ''
      }
    }
  },
  watch: {
    query: {
      handler () {
        this.fetchList()
      },
      deep: true
    }
  },
  mounted () {
    this.debouncedSearch = this.$debounce(() => {
      this.setQuery({ search: this.search || '', page: 1 })
    }, 300)
    this.fetchList()
    this.fetchCount()
  },
  beforeDestroy () {
    if (this.debouncedSearch) this.debouncedSearch.cancel()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    uploadStatusLabel,
    uploadChipStyle,
    withdrawNoteStatusLabel,
    withdrawNoteChipStyle,
    isWithdrawNoteStatusPlain,
    detailRoute (item) {
      return { name: 'BatchTransferDetail', params: { id: item.id } }
    },
    countFor (value) {
      const key = value || 'all'
      return this.counts[key] || 0
    },
    // ทุกการเปลี่ยน query วิ่งผ่านที่เดียว — watcher เป็นคนยิง fetch ไม่ต้องเรียกซ้ำเอง
    setQuery (patch) {
      this.query = { ...this.query, ...patch }
    },
    onSearchInput () {
      this.debouncedSearch()
    },
    onTabChange (value) {
      if (this.query.uploadStatus === value) return
      this.setQuery({ uploadStatus: value, page: 1 })
    },
    onPage (page) {
      this.setQuery({ page })
    },
    onLimit (limit) {
      this.setQuery({ limit, page: 1 })
    },
    async fetchList () {
      this.loading = true
      try {
        const { data } = await batchTransferProvider.paginate(this.query)
        if (data) {
          this.items = data.results || []
          this.totalItems = data.total || 0
        }
      } catch (error) {
        this.items = []
        this.totalItems = 0
        this.setSnackbar({
          value: true,
          message: (error && error.message) || 'โหลดรายการไม่สำเร็จ',
          type: 'error'
        })
      }
      this.loading = false
    },
    async fetchCount () {
      try {
        const { data } = await batchTransferProvider.getCount()
        if (data) this.counts = data
      } catch (error) {
        console.error('fetchCount', error)
      }
    },
    onUploaded (batch) {
      this.search = ''
      if (this.debouncedSearch) this.debouncedSearch.cancel()
      this.setQuery({ page: 1, search: '', uploadStatus: '' })
      this.fetchCount()

      const summary = batch.summary || {}
      const success = batch.uploadStatus === 'success'
      const message = success
        ? `${batch.fileName} สร้าง Withdraw Note แล้ว ${summary.withdrawNoteCount || 0} ใบ`
        : `${batch.fileName} ไม่ผ่านการตรวจสอบ ${summary.errorCount || 0} แถว`

      this.setSnackbar({
        value: true,
        message,
        type: success ? 'success' : 'error',
        timeout: 3200
      })
    }
  }
}
</script>

<style src="@/assets/css/batch-transfer.css">
</style>
